import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { TbFlask } from "react-icons/tb";
import { PageHeaderWithBack, PixPreview } from "../../../../Components/Molecules";
import { InitiatePixPayment, confirmPixBatch, confirmPixTransfer, createPixBatch } from "../../../../Hooks/UsePix";
import { getPixTransactionalLimits } from "../../../../Services/pixLimitsApi";
import { getPixSendFeeQuote } from "../../../../Services/pixFeeQuoteApi";
import { resolvePixBatchSplitForAmount } from "../../../../Utils/computePixBatchLegs";
import { resolvePixKeyApiType } from "../../../../Utils/detectPixKeyType";
import { PixTransferFinalStep } from "../../../../Components/Molecules/PixTransferSteps/PixTransferFinalStep";
import { PixDestinationForm } from "../../../../Components/Molecules/PixTransferSteps/PixDestinationForm";
import { PixTransferInfoStep } from "../../../../Components/Molecules/PixTransferSteps/PixTransferInfoStep";
import { PixTransferStepper } from "../../../../Components/Molecules/PixTransferSteps/PixTransferStepper";
import { PixTransferPasswordStep } from "../../../../Components/Molecules/PixTransferSteps/PixTransferPasswordStep";
import { buildPixTransferReceipt } from "../../../../Components/Molecules/PixTransferSteps/buildPixTransferReceipt";
import { getAccountTypeLabel, getStoredSelectedAccountId } from "../../../../Constants/accountTypes";
import { useTransferAccountBalance } from "../../../../Hooks/useTransferAccountBalance";
import { useBalance } from "../../../../Hooks/useBalance";
import useActiveIbAccount from "../../../../Hooks/useActiveIbAccount";
import { isDevelopmentOnly } from "../../../../Utils/developmentFeature";
import {
  ProgressOperationError,
  useProgressAlert,
} from "../../../../Hooks/useProgressAlert";
import { useToast } from "../../../../Hooks/toast";
import { useAccountData } from "../../../../Hooks";

const STEP_SUBTITLES = [
  "Escolha chave Pix ou copia e cola e informe o destino.",
  "Informe o valor da transferência.",
  "Revise os dados antes de confirmar a transferência.",
  "Cadastre ou confirme sua senha transacional para concluir.",
  "Transferência finalizada.",
];

export const PIX_DESTINATION_FORM_ID = "pix-transfer-destination-form";
export const PIX_TRANSFER_INFO_FORM_ID = "pix-transfer-info-form";

export const PixTransferContent = forwardRef(function PixTransferContent(
  {
    isActive = true,
    onCancel,
    onCloseModal,
    onStepChange,
    onDestinationSubViewChange,
    showGreeting = false,
    showDevPreview = false,
    footerInModal = false,
    /** When opening /pix/copia-cola, start on copia e cola mode. */
    initialTransferMode = "key",
  },
  ref
) {
  const {
    handlePOST: fetchInitiatePayment,
    state: initiatePaymentState,
    loading: initiatePaymentLoading,
    success: initiatePaymentSuccess,
    error: initiatePaymentError,
    message: initiatePaymentMessage,
    reset: resetInitiatePayment,
  } = InitiatePixPayment();
  const progress = useProgressAlert();
  const history = useHistory();
  const { addToast } = useToast();
  const { state: accountData } = useAccountData();
  const [step, setStep] = useState(0);
  const [transferMode, setTransferMode] = useState(initialTransferMode === "qrcode" ? "qrcode" : "key");
  const [destinationDraft, setDestinationDraft] = useState(null);
  const [linkValue, setLinkValue] = useState();
  const [sourceAccount, setSourceAccount] = useState(getStoredSelectedAccountId);
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmPaymentState, setConfirmPaymentState] = useState();
  const [pendingTransferAmount, setPendingTransferAmount] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const qrDecodeAdvancedRef = useRef(false);
  const { getBalance: refreshBalance } = useBalance();
  const { account: activeIbAccount } = useActiveIbAccount();
  const {
    spendable: sourceAccountBalanceCents,
    formattedBalance: sourceAccountBalance,
    formattedAvailable: sourceAccountFormattedAvailable,
    formattedLimit: sourceAccountFormattedLimit,
    formattedRemainingLimit: sourceAccountFormattedRemainingLimit,
    showLimit: sourceAccountShowLimit,
    loading: sourceAccountBalanceLoading,
    error: sourceAccountBalanceError,
    refresh: refreshSourceAccountBalance,
  } = useTransferAccountBalance(sourceAccount);
  const sourceAccountLabel =
    (activeIbAccount
      && String(activeIbAccount.id) === String(sourceAccount)
      && activeIbAccount.label)
    || getAccountTypeLabel(sourceAccount);
  const [infoStepCanContinue, setInfoStepCanContinue] = useState(false);
  const [previewCanContinue, setPreviewCanContinue] = useState(false);
  const [loteSplit, setLoteSplit] = useState(null);
  const [pixFeeQuote, setPixFeeQuote] = useState(null);
  const [typedAmountCents, setTypedAmountCents] = useState(0);
  const finalBalanceRefreshDone = useRef(false);
  const isConfirmingRef = useRef(false);
  const wasActiveRef = useRef(isActive);
  const transferSnapshotRef = useRef({});
  const passwordStepRef = useRef(null);
  const previewPrimaryActionRef = useRef(null);
  const pendingBatchIdRef = useRef(null);
  const pixAccountId = activeIbAccount?.id || sourceAccount;

  transferSnapshotRef.current = {
    initiatePaymentState,
    pendingTransferAmount,
    destinationDraft,
    sourceAccount: pixAccountId,
    loteSplit,
  };

  useEffect(() => {
    if (step < 4) {
      finalBalanceRefreshDone.current = false;
      return;
    }
    if (!confirmPaymentState || finalBalanceRefreshDone.current) return;

    finalBalanceRefreshDone.current = true;
    refreshSourceAccountBalance();
    refreshBalance();
  }, [step, confirmPaymentState, refreshSourceAccountBalance, refreshBalance]);

  const resetStates = useCallback(() => {
    if (isConfirmingRef.current) return;
    setStep(0);
    setTransferMode(initialTransferMode === "qrcode" ? "qrcode" : "key");
    setDestinationDraft(null);
    setLinkValue(undefined);
    setSourceAccount(getStoredSelectedAccountId());
    setConfirmPaymentState(undefined);
    setPendingTransferAmount(null);
    setPasswordError(null);
    setIsConfirming(false);
    setInfoStepCanContinue(false);
    setPreviewCanContinue(false);
    setLoteSplit(null);
    setPixFeeQuote(null);
    setTypedAmountCents(0);
    pendingBatchIdRef.current = null;
    isConfirmingRef.current = false;
    qrDecodeAdvancedRef.current = false;
    resetInitiatePayment();
  }, [resetInitiatePayment, initialTransferMode]);

  useEffect(() => {
    if (!wasActiveRef.current && isActive) {
      // Always bind to home active account when the flow opens.
      setSourceAccount(getStoredSelectedAccountId());
    }
    if (wasActiveRef.current && !isActive) {
      resetStates();
    }
    wasActiveRef.current = isActive;
  }, [isActive, resetStates]);

  const isDecodingQr =
    step === 0 && transferMode === "qrcode" && initiatePaymentLoading.POST;

  useEffect(() => {
    onStepChange?.({
      step,
      isConfirming,
      infoStepCanContinue,
      previewCanContinue,
      isDecodingQr,
    });
  }, [onStepChange, step, isConfirming, infoStepCanContinue, previewCanContinue, isDecodingQr]);

  const quoteAmountCents = Math.trunc(
    Number(
      pendingTransferAmount
        ?? linkValue
        ?? (typedAmountCents > 0 ? typedAmountCents : null)
        ?? initiatePaymentState?.amount,
    ) || 0,
  );

  useEffect(() => {
    if (activeIbAccount?.id) {
      setSourceAccount(activeIbAccount.id);
    }
  }, [activeIbAccount?.id]);

  useEffect(() => {
    if (!isActive || !pixAccountId) {
      setPixFeeQuote(null);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const quote = await getPixSendFeeQuote({
          accountId: pixAccountId,
          amountCents: quoteAmountCents > 0 ? quoteAmountCents : undefined,
        });
        if (!cancelled) setPixFeeQuote(quote);
      } catch {
        // Keep last good schedule; never replace a loaded overlay with a fake "Sem tarifa".
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isActive, pixAccountId, quoteAmountCents]);

  // If the live quote never landed, hydrate overlay from initiate preview.
  useEffect(() => {
    if (pixFeeQuote != null || !initiatePaymentState) return;
    const feePayer = initiatePaymentState.fee_payer ?? initiatePaymentState.feePayer;
    const feePercent = initiatePaymentState.fee_percent ?? initiatePaymentState.feePercent;
    const feeFixed = initiatePaymentState.fee_fixed ?? initiatePaymentState.feeFixed;
    const taxAmountCents = Math.trunc(Number(initiatePaymentState.tax_amount) || 0);
    if (feePayer == null && feePercent == null && feeFixed == null && taxAmountCents <= 0) return;
    setPixFeeQuote({
      feePayer: String(feePayer || "customer").toLowerCase() === "instance" ? "instance" : "customer",
      feePercent: Number(feePercent) || 0,
      feeFixed: Number(feeFixed) || 0,
      taxAmountCents,
      amountCents: Math.trunc(Number(initiatePaymentState.amount) || 0),
      totalDebitCents: Math.trunc(Number(initiatePaymentState.total_debit) || 0),
    });
  }, [initiatePaymentState, pixFeeQuote]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
      return;
    }
    history.push("/pix");
  }, [history, onCancel]);

  const handleTransferModeChange = useCallback(
    (mode) => {
      setTransferMode(mode);
      qrDecodeAdvancedRef.current = false;
      if (mode !== "qrcode") {
        resetInitiatePayment();
      }
    },
    [resetInitiatePayment]
  );

  const handleDestinationContinue = useCallback(
    (draft) => {
      setDestinationDraft(draft);
      qrDecodeAdvancedRef.current = false;

      if (draft.mode === "qrcode") {
        resetInitiatePayment();
        fetchInitiatePayment({
          mode: "qrcode",
          qr_code: draft.qrCode,
          account_id: pixAccountId || undefined,
        });
        return;
      }

      setStep(1);
    },
    [fetchInitiatePayment, resetInitiatePayment, pixAccountId]
  );

  // Copia e cola: after EMV decode succeeds, jump to review (skip amount step when QR has amount).
  useEffect(() => {
    if (destinationDraft?.mode !== "qrcode") return;
    if (!initiatePaymentSuccess.POST || !initiatePaymentState) {
      qrDecodeAdvancedRef.current = false;
      return;
    }
    if (qrDecodeAdvancedRef.current) return;
    qrDecodeAdvancedRef.current = true;

    const decodedAmount = initiatePaymentState.amount;
    if (decodedAmount != null) {
      setLinkValue(decodedAmount);
    }
    setStep(2);
  }, [destinationDraft?.mode, initiatePaymentSuccess.POST, initiatePaymentState]);

  const handleBackToDestination = useCallback(() => {
    qrDecodeAdvancedRef.current = false;
    resetInitiatePayment();
    setStep(0);
  }, [resetInitiatePayment]);

  const handleBackFromPreview = useCallback(() => {
    qrDecodeAdvancedRef.current = false;
    resetInitiatePayment();
    // Copia e cola skips the amount step — return to destination.
    if (destinationDraft?.mode === "qrcode") {
      setStep(0);
      return;
    }
    setStep(1);
  }, [resetInitiatePayment, destinationDraft?.mode]);

  useEffect(() => {
    if (step !== 2) return undefined;
    const amount = linkValue ?? initiatePaymentState?.amount;
    if (!amount || destinationDraft?.mode === "qrcode") {
      setLoteSplit(null);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const limits = await getPixTransactionalLimits();
        if (cancelled) return;
        const active = (limits.items || []).find(
          (item) => item.activeNow && !item.comingSoon,
        );
        const split = resolvePixBatchSplitForAmount(
          amount,
          limits.providerSplitCapCents,
          active?.perOperationCents ?? 0,
          active?.totalCents ?? 0,
          active?.availableCents ?? null,
          limits.spiMaxPerOperationCents,
        );
        setLoteSplit(split?.count > 1 ? split : null);
      } catch {
        if (!cancelled) setLoteSplit(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [step, linkValue, initiatePaymentState?.amount, destinationDraft?.mode]);

  const handlePreviewConfirm = useCallback(({ amount }) => {
    setPendingTransferAmount(amount);
    setPasswordError(null);
    setStep(3);
  }, []);

  const handleBackFromPassword = useCallback(() => {
    setPasswordError(null);
    setStep(2);
  }, []);

  const handleGoBack = useCallback(() => {
    if (step === 1) {
      handleBackToDestination();
      return;
    }
    if (step === 2) {
      handleBackFromPreview();
      return;
    }
    if (step === 3) {
      handleBackFromPassword();
    }
  }, [step, handleBackToDestination, handleBackFromPreview, handleBackFromPassword]);

  const handleSubmitCurrentStep = useCallback(() => {
    if (step === 2) {
      previewPrimaryActionRef.current?.();
    }
  }, [step]);

  const handleConfirmPassword = useCallback(() => {
    passwordStepRef.current?.confirm();
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      goBack: handleGoBack,
      submit: handleSubmitCurrentStep,
      confirmPassword: handleConfirmPassword,
      reset: resetStates,
    }),
    [handleGoBack, handleSubmitCurrentStep, handleConfirmPassword, resetStates]
  );

  const handleFinishTransaction = useCallback(
    async (pass) => {
      const snapshot = transferSnapshotRef.current;
      const paymentState = snapshot.initiatePaymentState;
      const amount = snapshot.pendingTransferAmount ?? paymentState?.amount;
      const pix_id = paymentState?.pix_id;
      const documentToValidate = paymentState?.documentToValidate;
      const lote = snapshot.loteSplit;
      const draft = snapshot.destinationDraft;

      if (amount == null) return { success: false };
      if (!lote && !pix_id) return { success: false };
      if (isConfirmingRef.current) return { success: false };

      isConfirmingRef.current = true;
      setIsConfirming(true);
      setPasswordError(null);

      const beneficiaryName = paymentState?.receiver?.name ?? "destinatário";
      const receiptContext = {
        initiateState: paymentState,
        amount,
        destinationDraft: draft,
        sourceAccountLabel,
      };

      const sendTransfer = async () => {
        if (lote?.count > 1 && draft?.mode !== "qrcode") {
          let batchId = pendingBatchIdRef.current;
          let createdData = null;
          if (!batchId) {
            const created = await createPixBatch({
              key: {
                type:
                  resolvePixKeyApiType(draft.pixKeyType, draft.keyValue) ||
                  draft.pixKeyType,
                value: draft.keyValue,
              },
              amount,
              account_id: snapshot.sourceAccount || undefined,
            });
            if (!created.success) return created;
            batchId = created.data?.batch_id;
            createdData = created.data;
            if (!batchId) {
              return {
                success: false,
                message: "Não foi possível criar o lote Pix",
                description: "Tente novamente.",
              };
            }
            pendingBatchIdRef.current = batchId;
          }
          const confirmed = await confirmPixBatch({
            batch_id: batchId,
            password_combination: pass,
            account_id: snapshot.sourceAccount || undefined,
          });
          if (!confirmed.success) return confirmed;
          pendingBatchIdRef.current = null;
          return {
            success: true,
            data: {
              ...createdData,
              ...confirmed.data,
              batch_id: confirmed.data?.batch_id ?? confirmed.data?.batchId ?? batchId,
            },
          };
        }

        return confirmPixTransfer({
          pix_id,
          amount,
          password_combination: pass,
          documentToValidate,
          receiptContext,
        });
      };

      try {
        const isBatchTransfer = lote?.count > 1 && draft?.mode !== "qrcode";
        const result = await progress.runWithProgress(sendTransfer, {
            loading: isBatchTransfer ? "Enviando lote Pix..." : "Finalizando transferência...",
            loadingDescription: isBatchTransfer
              ? `Serão ${lote.count} Pix para ${beneficiaryName}`
              : `Enviando Pix para ${beneficiaryName}`,
            success: isBatchTransfer ? "Lote Pix enviado" : "Transferência concluída",
            successDescription: isBatchTransfer
              ? `Acompanhe o processamento na página do lote.`
              : "Seu Pix foi enviado com sucesso.",
            error: "Falha na transferência",
            errorDescription: "Não foi possível concluir a transferência. Tente novamente.",
          }
        );

        if (isBatchTransfer) {
          const resolvedBatchId =
            result?.data?.batch_id
            ?? result?.data?.batchId
            ?? pendingBatchIdRef.current;
          pendingBatchIdRef.current = null;
          if (resolvedBatchId) {
            history.push(`/pix/lotes/${encodeURIComponent(resolvedBatchId)}`);
            return { success: true };
          }
          addToast({
            type: "info",
            title: "Lote Pix enviado",
            description: "Acompanhe o processamento na página de lotes.",
            linkTo: "/pix/lotes",
            linkLabel: "Ver lotes",
          });
          return { success: true };
        }

        setConfirmPaymentState(
          buildPixTransferReceipt({
            ...receiptContext,
            apiData: result?.data,
          })
        );
        setStep(4);

        return { success: true };
      } catch (error) {
        if (error instanceof ProgressOperationError) {
          const nextError = {
            title: error.result.message ?? "Aviso",
            description: error.result.description || null,
          };
          setPasswordError(nextError);
          return { success: false, ...nextError };
        }

        const nextError = {
          title: "Falha na transferência",
          description:
            error instanceof Error ? error.message : "Não foi possível concluir a transferência.",
        };
        setPasswordError(nextError);
        return { success: false, ...nextError };
      } finally {
        isConfirmingRef.current = false;
        setIsConfirming(false);
      }
    },
    [progress, sourceAccountLabel, history, addToast]
  );

  const isCustomAmount =
    destinationDraft?.mode === "qrcode" &&
    (initiatePaymentState?.amount === 0 || !initiatePaymentState?.amount);

  const qrDecodeError = (() => {
    if (
      step !== 0 ||
      transferMode !== "qrcode" ||
      destinationDraft?.mode !== "qrcode" ||
      !initiatePaymentError.POST
    ) {
      return null;
    }

    const msg = initiatePaymentMessage.POST;
    const detail =
      msg?.information ||
      msg?.detail ||
      (msg?.description && msg.description !== "Tente novamente." ? msg.description : null);

    return {
      title: detail || msg?.title || "Não foi possível decodificar o código Pix",
      description: "Verifique o código EMV e tente novamente.",
    };
  })();

  return (
    <div className="pix-transfer-content min-w-0 w-full">
      {showGreeting && (
        <PageHeaderWithBack
          title="Transferência Pix"
          subTitle={STEP_SUBTITLES[step]}
          {...(step < 4 ? { onBack: handleCancel } : {})}
        />
      )}

      {showDevPreview && isDevelopmentOnly && step < 4 && (
        <Link
          to="/pix/transferencia/preview"
          className="mb-4 inline-flex items-center gap-1.5 rounded-lg border border-dashed border-amber-300 bg-amber-50/60 px-3 py-2 text-xs font-medium text-amber-900 transition hover:bg-amber-50"
        >
          <TbFlask className="h-3.5 w-3.5" />
          Ver tela de conclusão (mock, sem tirar saldo)
        </Link>
      )}

      {step < 4 ? (
        <>
          <PixTransferStepper currentStep={step} />

          {step === 0 && (
            <PixDestinationForm
              transferMode={transferMode}
              onTransferModeChange={handleTransferModeChange}
              initialDraft={destinationDraft}
              onContinue={handleDestinationContinue}
              formId={footerInModal ? PIX_DESTINATION_FORM_ID : undefined}
              hideFooter={footerInModal}
              onBack={footerInModal ? handleCancel : undefined}
              onSubViewChange={onDestinationSubViewChange}
              decoding={isDecodingQr}
              decodeError={qrDecodeError}
            />
          )}

          {step === 1 && destinationDraft && destinationDraft.mode !== "qrcode" && (
            <PixTransferInfoStep
              sourceAccount={pixAccountId || sourceAccount}
              sourceAccountBalance={sourceAccountBalance}
              sourceAccountBalanceCents={sourceAccountBalanceCents}
              sourceAccountBalanceLoading={sourceAccountBalanceLoading}
              sourceAccountBalanceError={sourceAccountBalanceError}
              sourceAccountShowLimit={sourceAccountShowLimit}
              sourceAccountFormattedAvailable={sourceAccountFormattedAvailable}
              sourceAccountFormattedLimit={sourceAccountFormattedLimit}
              sourceAccountFormattedRemainingLimit={sourceAccountFormattedRemainingLimit}
              destinationDraft={destinationDraft}
              handleFetchInitialPayment={fetchInitiatePayment}
              loadingFetchInitialPayment={initiatePaymentLoading.POST}
              successFetchInitialPayment={initiatePaymentSuccess.POST}
              errorFetchInitialPayment={initiatePaymentError.POST}
              errorMessage={initiatePaymentMessage.POST}
              onSetTransferAmount={setLinkValue}
              onTypedAmountCentsChange={setTypedAmountCents}
              onBack={handleBackToDestination}
              onSuccess={() => setStep(2)}
              onCanContinueChange={setInfoStepCanContinue}
              formId={footerInModal ? PIX_TRANSFER_INFO_FORM_ID : undefined}
              hideFooter={footerInModal}
              feeQuote={pixFeeQuote}
            />
          )}

          {step === 2 && Boolean(initiatePaymentState) && (
            <PixPreview
              isCustomValue={isCustomAmount}
              isLoading={isConfirming}
              onConfirmClick={handlePreviewConfirm}
              cancelOperation={handleCancel}
              onBack={handleBackFromPreview}
              previewData={initiatePaymentState}
              amount={linkValue}
              sourceAccountLabel={sourceAccountLabel}
              sourceAccountBalance={sourceAccountBalance}
              sourceAccountBalanceCents={sourceAccountBalanceCents}
              sourceAccountBalanceLoading={sourceAccountBalanceLoading}
              feeQuote={pixFeeQuote}
              className="app-card w-full overflow-hidden"
              hideFooter={footerInModal}
              loteSplit={loteSplit}
              onCanContinueChange={setPreviewCanContinue}
              onRegisterPrimaryAction={(action) => {
                previewPrimaryActionRef.current = action;
              }}
            />
          )}

          {step === 3 && (
            <PixTransferPasswordStep
              ref={passwordStepRef}
              onSubmit={handleFinishTransaction}
              onBack={isConfirming ? undefined : handleBackFromPassword}
              error={passwordError}
              loading={isConfirming}
              onDismissError={() => setPasswordError(null)}
              hideFooter={footerInModal}
              transactionalPasswordQualified={
                accountData?.userId
                  ? accountData.transactionalPasswordQualified === true
                  : undefined
              }
              stepUpOptions={{
                transactionId: initiatePaymentState?.pix_id,
                amount: pendingTransferAmount ?? initiatePaymentState?.amount,
              }}
            />
          )}
        </>
      ) : (
        !isConfirming && (
          <PixTransferFinalStep
            transactionStatus={!!confirmPaymentState}
            transactionData={confirmPaymentState}
            onNewTransfer={resetStates}
            onDismiss={onCloseModal}
            sourceAccountLabel={sourceAccountLabel}
            sourceAccountBalance={sourceAccountBalance}
            sourceAccountBalanceLoading={sourceAccountBalanceLoading}
            hideToolbar={footerInModal}
          />
        )
      )}
    </div>
  );
});
